import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight, Building, Layers, Loader2, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { useUpdateUser, useUserDetails } from '../api';
import {
  useGroups,
  useLocations,
  useTtsPlans,
  useUserModules,
  useVoicePlans,
} from '../lookups/api';
import { asFormString, fromLegacyExpiry, toLegacyExpiry } from '../format';
import { updateSubUserSchema, type UpdateSubUserInput } from '../schema';

const STEP_FIELDS: Record<number, Array<keyof UpdateSubUserInput>> = {
  0: ['name', 'emailid', 'number'],
  1: ['company', 'address', 'pincode'],
  2: ['accountType', 'expiryDate', 'planId', 'moduleIds', 'ttsPlanId', 'ttsCredits', 'groupIds', 'locationIds'],
  3: [],
};

const STEPS = [
  { id: 'contact', label: 'Contact', description: 'Identification', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company & address', icon: Building },
  { id: 'access', label: 'Access & Resources', description: 'Plan, modules, allocations', icon: Layers },
  { id: 'review', label: 'Review', description: 'Confirm changes' },
] as const;

const TTS_MODULE_IDS = new Set(['7', '10']);

export function UpdateSubUserPage() {
  const navigate = useNavigate();
  const { userId: targetId } = useParams<{ userId: string }>();
  const targetUserId = Number(targetId);
  const parentId = Number(useAuthStore((s) => s.userId));

  const profile = useUserDetails(targetUserId);
  const voicePlans = useVoicePlans(parentId);
  const ttsPlans = useTtsPlans(parentId);
  // The parent USER can only grant modules they themselves hold.
  const modules = useUserModules(parentId);
  const groups = useGroups();
  const locations = useLocations();

  const update = useUpdateUser();
  const stepper = useStepper(STEPS.length);

  const form = useForm<UpdateSubUserInput>({
    resolver: zodResolver(updateSubUserSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      accountType: '0',
      expiryDate: '',
      planId: '',
      ttsPlanId: '',
      moduleIds: [],
      ttsCredits: '',
      groupIds: [],
      locationIds: [],
    },
  });

  // Prefill from /api/obd/user/profile/{userId}.
  useEffect(() => {
    if (!profile.data) return;
    const p = profile.data;
    form.reset({
      name: asFormString(p.name),
      emailid: asFormString(p.emailid),
      number: asFormString(p.number),
      company: asFormString(p.company),
      address: asFormString(p.address),
      pincode: asFormString(p.pincode),
      accountType: String(p.accountType ?? 0) as '0' | '1',
      expiryDate: fromLegacyExpiry(p.expiryDate)?.toISOString() ?? '',
      planId: asFormString(p.planId),
      ttsPlanId: asFormString(p.ttsPlanId),
      moduleIds: (p.modules ?? []).map((m) => String(m.moduleId)),
      ttsCredits: asFormString(p.ttsCredits),
      groupIds: (p.groups ?? []).map((g) => String(g.groupId)),
      locationIds: (p.locations ?? []).map((l) => String(l.locationId)),
    });
  }, [profile.data, form]);

  const selectedModuleIds = form.watch('moduleIds');
  const ttsEnabled = useMemo(
    () => selectedModuleIds.some((id) => TTS_MODULE_IDS.has(id)),
    [selectedModuleIds],
  );

  const moduleOptions = useMemo(
    () =>
      (modules.data ?? []).map((m) => ({
        value: String(m.moduleId),
        label: m.moduleName,
      })),
    [modules.data],
  );
  const groupOptions = useMemo(
    () => (groups.data ?? []).map((g) => ({ value: String(g.groupId), label: g.groupName })),
    [groups.data],
  );
  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((l) => ({
        value: String(l.locationId),
        label: l.locationName,
      })),
    [locations.data],
  );

  const handleNext = async () => {
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: UpdateSubUserInput) => {
    update.mutate(
      {
        userId: targetUserId,
        parent: parentId,
        username: profile.data?.username ?? '',
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address?.trim() ?? '',
        pincode: input.pincode?.trim() ?? '',
        accountType: Number(input.accountType) as 0 | 1,
        userType: 'suser',
        expiryDate: toLegacyExpiry(input.expiryDate),
        planId: Number(input.planId),
        ttsPlanId:
          ttsEnabled && input.ttsPlanId ? Number(input.ttsPlanId) : undefined,
        ttsCredits: ttsEnabled ? (input.ttsCredits || '0') : '0',
        moduleId: input.moduleIds.length > 0 ? input.moduleIds.join(',') : null,
        groupRows:
          input.groupIds.length > 0
            ? JSON.stringify(
                input.groupIds.map((id) => {
                  const g = groupOptions.find((o) => o.value === id);
                  return { groupId: Number(id), groupName: g?.label ?? '' };
                }),
              )
            : undefined,
        locationRows:
          input.locationIds.length > 0
            ? JSON.stringify(
                input.locationIds.map((id) => {
                  const l = locationOptions.find((o) => o.value === id);
                  return { locationId: Number(id), locationName: l?.label ?? '' };
                }),
              )
            : undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Sub user updated');
          navigate('/sub-user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not update the sub user.');
        },
      },
    );
  };

  if (profile.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Update Sub User" description="Loading…" />
        <FormSkeleton />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update Sub User"
        description={
          profile.data ? `Edit ${profile.data.username}'s details.` : 'Edit a sub user.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/sub-user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {stepper.current === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contact</CardTitle>
                <CardDescription>Identification and direct contact details.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Full name<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Email<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Phone<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          placeholder="10-digit mobile number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
                <CardDescription>Company and mailing address.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>
                        Company<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>
                        Address<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Pincode<span className="ml-0.5 text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} inputMode="numeric" maxLength={10} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 2 && (
            <AccessAndResourcesStep
              form={form}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              ttsEnabled={ttsEnabled}
              isLoadingPlans={voicePlans.isLoading}
              isLoadingTtsPlans={ttsPlans.isLoading}
              isLoadingModules={modules.isLoading}
            />
          )}

          {stepper.current === 3 && (
            <ReviewStep
              form={form}
              voicePlans={voicePlans.data ?? []}
              ttsPlans={ttsPlans.data ?? []}
              moduleOptions={moduleOptions}
              groupOptions={groupOptions}
              locationOptions={locationOptions}
              ttsEnabled={ttsEnabled}
            />
          )}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || update.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/sub-user-list">Cancel</Link>
              </Button>
              {!stepper.isLast ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button type="submit" disabled={update.isPending}>
                  {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save changes
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

interface AccessStepProps {
  form: ReturnType<typeof useForm<UpdateSubUserInput>>;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
  isLoadingPlans: boolean;
  isLoadingTtsPlans: boolean;
  isLoadingModules: boolean;
}

function AccessAndResourcesStep({
  form,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  ttsEnabled,
  isLoadingPlans,
  isLoadingTtsPlans,
  isLoadingModules,
}: AccessStepProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Plan &amp; access</CardTitle>
          <CardDescription>Billing mode, validity, and voice plan.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="accountType"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>
                  Account type<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="grid grid-cols-2 gap-3"
                  >
                    {(
                      [
                        { value: '0', label: 'Promotional' },
                        { value: '1', label: 'Transactional' },
                      ] as const
                    ).map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring"
                      >
                        <RadioGroupItem value={opt.value} />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expiryDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Validity expires on<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value ? new Date(field.value) : null}
                    onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                    fromDate={new Date()}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="planId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Voice plan<span className="ml-0.5 text-destructive">*</span>
                </FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={isLoadingPlans}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingPlans ? 'Loading…' : 'Pick a voice plan'} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {voicePlans.map((p) => (
                      <SelectItem key={p.planId} value={String(p.planId)}>
                        {p.planName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modules</CardTitle>
          <CardDescription>
            Capabilities. Selecting a TTS module enables TTS plan and credit fields.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={form.control}
            name="moduleIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assigned modules</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={moduleOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={isLoadingModules ? 'Loading modules…' : 'Pick modules'}
                    searchPlaceholder="Filter modules…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {ttsEnabled && (
            <div className="grid gap-4 rounded-md border bg-muted/30 p-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="ttsPlanId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TTS plan</FormLabel>
                    <Select
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      disabled={isLoadingTtsPlans}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={isLoadingTtsPlans ? 'Loading…' : 'Pick a TTS plan'}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ttsPlans.map((p) => (
                          <SelectItem key={p.planId} value={String(p.planId)}>
                            {p.planName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ttsCredits"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>TTS credits (₹)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allocations</CardTitle>
          <CardDescription>
            Restrict this sub user to specific groups and locations they can target.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="groupIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Groups</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={groupOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Any group"
                    searchPlaceholder="Filter groups…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="locationIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Locations</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={locationOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Any location"
                    searchPlaceholder="Filter locations…"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}

interface ReviewStepProps {
  form: ReturnType<typeof useForm<UpdateSubUserInput>>;
  voicePlans: Array<{ planId: number; planName: string }>;
  ttsPlans: Array<{ planId: number; planName: string }>;
  moduleOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  locationOptions: Array<{ value: string; label: string }>;
  ttsEnabled: boolean;
}

function ReviewStep({
  form,
  voicePlans,
  ttsPlans,
  moduleOptions,
  groupOptions,
  locationOptions,
  ttsEnabled,
}: ReviewStepProps) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  const planName = voicePlans.find((p) => String(p.planId) === v.planId)?.planName ?? '—';
  const ttsPlanName = ttsPlans.find((p) => String(p.planId) === v.ttsPlanId)?.planName ?? '—';
  const moduleLabels = v.moduleIds
    .map((id) => moduleOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const groupLabels = v.groupIds
    .map((id) => groupOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');
  const locationLabels = v.locationIds
    .map((id) => locationOptions.find((o) => o.value === id)?.label ?? id)
    .join(', ');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the changes. Click any earlier step header to edit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Contact">
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address ?? ''} multiline />
          <Row label="Pincode" value={v.pincode ?? ''} />
        </ReviewSection>
        <ReviewSection title="Access">
          <Row
            label="Account type"
            value={v.accountType === '1' ? 'Transactional' : 'Promotional'}
          />
          <Row label="Validity expires" value={expiry} />
          <Row label="Voice plan" value={planName} />
          <Row label="Modules" value={moduleLabels || '—'} multiline />
          {ttsEnabled && (
            <>
              <Row label="TTS plan" value={ttsPlanName} />
              <Row label="TTS credits" value={`₹${v.ttsCredits || '0'}`} />
            </>
          )}
        </ReviewSection>
        <ReviewSection title="Allocations">
          <Row label="Groups" value={groupLabels || 'Any'} multiline />
          <Row label="Locations" value={locationLabels || 'Any'} multiline />
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((s) => (
        <Card key={s}>
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-5 w-1/4" />
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
